import React from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';

import useAppContext from 'flow/hooks/useAppContext';
import { setViewToFullscreen } from 'flow/actions/meeting-actions';
// IconButton despite there being no icon: it is BaseButton plus a tooltip, and
// the tooltip is the point — it carries the reason this button is disabled.
// Button has the variants but no tooltip of its own.
import IconButton from 'flow/components/ui-components/IconButton';
import { selectIsSameLobbyAsActiveMeeting } from 'flow/selectors/meeting-selectors';
import useJoinMeetingErrorText from 'flow/hooks/useJoinMeetingErrorText';
import Skeleton from 'flow/components/ui-components/Skeleton';
import { ViewerMeetingRole } from 'flow/hooks/useViewerMeetingRole';
import { twMerge } from 'tailwind-merge';

// Shared by the button and the skeleton that stands in for it, so the row does
// not jump when the label arrives. The min width holds both "Join" and the
// longer "Ask to join", since which one it will be is the thing we are waiting
// to find out.
const JOIN_BUTTON_CLASS =
    'h-10 min-w-36 shrink-0 rounded-2xl px-6 text-sm font-bold';

interface Props {
    name: string;
    onJoinMeeting?: () => void;
    /** Asked once by MeetingLobby and handed down — see useViewerMeetingRole. */
    viewerMeetingRole: ViewerMeetingRole;
}

/**
 * The button that gets you in: join outright, ask to be let in (VID-76), or the
 * skeleton that stands in while we find out which of those it will be.
 *
 * Separate from the wait it can lead to, so that none of the work behind it —
 * the meeting-role lookup, the reasons you cannot join yet — runs for someone
 * already standing at the door with nothing to decide.
 */
const LobbyButtonJoin = ({ name, onJoinMeeting, viewerMeetingRole }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const sameLobbyAsActiveMeeting = useAppSelector(
        selectIsSameLobbyAsActiveMeeting,
    );
    const { viewerMustAskToJoin, meetingRoleIsPending, meetingRole } =
        viewerMeetingRole;
    const canNotJoinReason = useJoinMeetingErrorText(name);

    // Someone who has to be let in asks rather than joins outright (VID-76).
    // Home decides that, not us; selectViewerMustAskToJoin falls back to a guess
    // only once the answer is known not to be coming.
    const joinLabel = viewerMustAskToJoin
        ? i18next.t('videoconference.lobby.ask_to_join')
        : i18next.t('videoconference.lobby.join');

    // Held back rather than labelled on a guess: this button is a promise about
    // what happens next, and correcting "Join" to "Ask to join" under someone
    // who is already reaching for it changes what they think they are doing.
    // Only ever a moment, and only for the logged-in — a guest asks, always.
    if (meetingRoleIsPending && !sameLobbyAsActiveMeeting) {
        return (
            <Skeleton>
                <div
                    className={twMerge(JOIN_BUTTON_CLASS, 'bg-current')}
                    data-test-id="meeting-join-button-skeleton"
                />
            </Skeleton>
        );
    }

    return (
        <IconButton
            data-test-id="meeting-join-button"
            // What home said this viewer is. A label that turns out wrong is
            // otherwise untraceable from outside the app, and home has been
            // seen naming a member EXTERNAL (VID-430).
            data-test-meeting-role={meetingRole ?? 'none'}
            className={twMerge(
                JOIN_BUTTON_CLASS,
                'bg-green text-white',
                !!canNotJoinReason && 'cursor-not-allowed',
            )}
            onClick={() =>
                sameLobbyAsActiveMeeting
                    ? dispatch(setViewToFullscreen())
                    : onJoinMeeting?.()
            }
            disabled={sameLobbyAsActiveMeeting ? false : !!canNotJoinReason}
            tooltipLabel={canNotJoinReason}
        >
            {sameLobbyAsActiveMeeting
                ? i18next.t('videoconference.header.fullscreen')
                : joinLabel}
        </IconButton>
    );
};

export default LobbyButtonJoin;
